// Floating inspector: a compact popover pinned near the selected controller so
// eyes stay on the canvas. The side panel card remains as the fallback.

import type { RefObject } from 'react'
import type { Controller } from '../core/types'
import { controllerSpine, propagateSync, setSyncAcrossChannels } from '../core/curves'
import { useStore } from '../state/store'
import { CHANNELS, CHANNEL_RANGES, ControllerFields } from './SidePanel'
import { IconCenter, IconClose, IconTrash } from './icons'

export function Inspector({ wrapRef }: { wrapRef: RefObject<HTMLDivElement | null> }) {
  const doc = useStore((s) => s.doc)
  const activeChannel = useStore((s) => s.activeChannel)
  const selectedController = useStore((s) => s.selectedController)
  const setSelectedController = useStore((s) => s.setSelectedController)
  const view = useStore((s) => s.view)
  const commit = useStore((s) => s.commit)

  const c = doc.channels[activeChannel].controllers.find((x) => x.id === selectedController)
  if (!c || !view) return null

  const spine = controllerSpine(c)
  const anchor = spine[Math.floor(spine.length / 2)]
  const sx = anchor.x * view.scale + view.tx
  const sy = anchor.y * view.scale + view.ty

  const wrap = wrapRef.current?.getBoundingClientRect()
  const W = 236
  const H = 250
  let left = sx + 18
  let top = sy - 10
  if (wrap) {
    if (left + W > wrap.width - 8) left = sx - W - 18
    left = Math.max(8, Math.min(left, wrap.width - W - 8))
    top = Math.max(8, Math.min(top, wrap.height - H - 8))
  }

  const range = CHANNEL_RANGES[activeChannel]
  const channelLabel = CHANNELS.find((x) => x.id === activeChannel)?.label ?? activeChannel

  const onChange = (label: string, up: (x: Controller) => void) =>
    commit(label, (d) => {
      const target = d.channels[activeChannel].controllers.find((x) => x.id === c.id)
      if (target) up(target)
    })

  return (
    <div
      className="inspector holo-frame"
      style={{ left, top }}
      role="dialog"
      aria-label={`${channelLabel} ${c.kind} controller`}
    >
      <div className="inspector-head">
        <span className="inspector-title">
          <span className="inspector-dot" style={{ background: `var(--ch-${activeChannel})` }} />
          {channelLabel} {c.kind}
        </span>
        <button
          className="quiet icon-btn"
          title="Center on canvas"
          aria-label="Center controller on canvas"
          onClick={() =>
            commit('Center controller', (d) => {
              const target = d.channels[activeChannel].controllers.find((x) => x.id === c.id)
              if (!target) return
              // Reference: the point itself, a line's midpoint, or the node average.
              let ref = target.a
              if (target.kind === 'line') {
                ref = { x: (target.a.x + target.b.x) / 2, y: (target.a.y + target.b.y) / 2 }
              } else if (target.nodes.length > 0) {
                const n = target.nodes.length
                ref = {
                  x: target.nodes.reduce((s, p) => s + p.x, 0) / n,
                  y: target.nodes.reduce((s, p) => s + p.y, 0) / n,
                }
              }
              const dx = d.canvas.width / 2 - ref.x
              const dy = d.canvas.height / 2 - ref.y
              target.a = { x: target.a.x + dx, y: target.a.y + dy }
              target.b = { x: target.b.x + dx, y: target.b.y + dy }
              target.nodes = target.nodes.map((p) => ({ x: p.x + dx, y: p.y + dy }))
              propagateSync(d, target.id)
            })
          }
        >
          <IconCenter size={13} />
        </button>
        <button
          className="quiet icon-btn"
          onClick={() => setSelectedController(null)}
          aria-label="Close inspector (Esc)"
          title="Close (Esc)"
        >
          <IconClose size={12} />
        </button>
      </div>
      <ControllerFields
        controller={c}
        range={range}
        compact
        onChange={onChange}
        onToggleSync={(on) =>
          commit(on ? 'Sync controller across channels' : 'Unsync controller', (d) => {
            setSyncAcrossChannels(d, activeChannel, c.id, on)
          })
        }
      />
      <div className="inspector-row">
        <label className="mini">
          <input
            type="checkbox"
            checked={c.enabled}
            onChange={(e) => onChange('Toggle controller', (x) => (x.enabled = e.target.checked))}
          />
          enabled
        </label>
        <button
          className="danger icon-btn quiet"
          onClick={() => {
            commit('Delete controller', (d) => {
              const list = d.channels[activeChannel].controllers
              d.channels[activeChannel].controllers = list.filter((x) => x.id !== c.id)
            })
            setSelectedController(null)
          }}
          aria-label="Delete controller"
        >
          <IconTrash size={13} />
          Delete
        </button>
      </div>
    </div>
  )
}
